import * as React from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import Image from 'next/image'
import type {JSX} from 'react'
import type {Block} from '@/_new-code/services/kontent-ai/types'
import {
	ReusableCTAButtonComponent,
	type CtaButtonContentItem,
} from '@/_new-code/products/flexible-web-toolkit/blocks/cta-button'
import {
	RichTextBlock,
	type RichTextContentItem,
} from '@/_new-code/products/flexible-web-toolkit/blocks/rich-text'
import {IconListBlock} from '@/_new-code/products/flexible-web-toolkit/blocks/icon-list'
import type {IconListContentItem} from '@/_new-code/products/flexible-web-toolkit/blocks/icon-list'
import {loadImageFromKontentAI} from '@/imageLoader'

export type ListSectionContentItem = IContentItem<{
	title: Elements.TextElement
	iconList: Elements.LinkedItemsElement<IconListContentItem>
}>

export type ProductPortfolioContentItem = IContentItem<{
	cardRichText: Elements.LinkedItemsElement<RichTextContentItem>
	headerImage: Elements.AssetsElement
	infoButton: Elements.LinkedItemsElement<CtaButtonContentItem>
	listSection: Elements.LinkedItemsElement<ListSectionContentItem>
}>

export const ListSectionBlock: Block<ListSectionContentItem> = ({
	block,
	...context
}) => {
	return (
		<>
			<h4>{block.elements.title}</h4>
			<div className="-mt-8">
				{block.elements.iconList[0] ? (
					<IconListBlock
						block={block.elements.iconList[0]}
						{...context}
					/>
				) : null}
			</div>
		</>
	)
}

interface ContentProps {
	className?: string
	children: React.ReactNode
}

const Content = ({className = '', children}: ContentProps): JSX.Element => {
	return (
		<div className={className}>
			<div>{children}</div>
		</div>
	)
}

interface IconsProps {
	className?: string
	children: React.ReactNode
	title?: string
}

const Icons = ({
	className = '',
	children,
	title = '',
}: IconsProps): JSX.Element => {
	return (
		<div className={` ${className}`}>
			<h4>{title}</h4>
			<div className="mt-4 flex flex-wrap">{children}</div>
		</div>
	)
}

interface ProductPortfolioProps {
	className?: string
	children: React.ReactNode
}

const ProductPortfolio = ({
	className = '',
	children,
}: ProductPortfolioProps): JSX.Element => {
	const childrenArray = React.Children.toArray(
		children
	) as React.ReactElement[]

	const content = childrenArray.filter((child) => {
		return child.type === Content
	})

	const icons = childrenArray.filter((child) => {
		return child.type === Icons
	})

	const image = childrenArray.filter((child) => {
		return child.type === Image
	})

	const buttons = childrenArray.filter((child) => {
		return (
			child.type !== Icons &&
			child.type !== Content &&
			child.type !== Image
		)
	})

	return (
		<div className={`rounded-lg shadow-secondary md:flex${className}`}>
			<div className="bg-gray-200 px-4 py-10 pb-10 md:w-1/2 md:pl-6 md:pr-16">
				<div className="content">{content}</div>
				<div className="hidden flex-col flex-wrap items-center text-sm md:flex md:flex-row">
					{buttons}
				</div>
			</div>
			<div className="md:w-1/2">
				<div className="md:-mt-[20%] md:px-10">{image}</div>
				<div className="p-6">{icons}</div>
			</div>
			<div className="flex flex-wrap items-center justify-center md:hidden">
				{buttons}
			</div>
		</div>
	)
}

export const ProductPortfolioBlock: Block<ProductPortfolioContentItem> = ({
	block,
	...context
}) => {
	return (
		<div className="container-wide mx-auto mt-32">
			<ProductPortfolio>
				<Content>
					{block.elements.cardRichText[0] ? (
						<RichTextBlock
							block={block.elements.cardRichText[0]}
							{...context}
						/>
					) : null}
				</Content>
				{block.elements.infoButton[0] ? (
					<div className="px-8 py-4">
						<ReusableCTAButtonComponent
							block={block.elements.infoButton[0]}
							{...context}
						/>
					</div>
				) : null}
				<Icons>
					<div className="relative content-center">
						{block.elements.headerImage[0] ? (
							<Image
								alt={
									block.elements.headerImage[0].description ||
									''
								}
								className="p-4"
								fill
								loader={loadImageFromKontentAI}
								src={block.elements.headerImage[0].url}
							/>
						) : (
							<div className="m-5 p-10" />
						)}
						{block.elements.listSection.length > 0
							? block.elements.listSection.map((li) => (
									<ListSectionBlock
										block={li}
										key={li.system.id}
										{...context}
									/>
								))
							: null}
					</div>
				</Icons>
			</ProductPortfolio>
		</div>
	)
}
